<template>
  <UserShell
    title="首页概览"
    subtitle="管理您的主播团队与资产评估"
    :profile="profile"
    :menu-items="menuItems"
    active-key="overview"
    center-path="/user/org"
  >
    <div class="hero-row">
      <button type="button" class="hero-avatar-btn" @click="triggerAvatarUpload">
        <img class="hero-avatar" :src="profile.avatar" alt="机构头像" />
        <div class="hero-avatar-mask">
			 <img class="hero-avatar-mask-icon" src="../../assets/img/user/bj.png" />
			替换图片
		</div>
      </button>
      <input ref="avatarUploader" type="file" class="hidden-input" accept=".jpg,.jpeg,.png" @change="handleAvatarChange" />
      <div class="hero-meta">
        <div class="hero-name-row">
          <div class="hero-name">{{ companyInfo.name }}</div>
		  <img class="mcn-tag" src="../../assets/img/user/jg_tag.png"/>
        </div>
        <div class="hero-subline">入驻时间：{{ companyInfo.entryDate }}</div>
      </div>
    </div>

    <div class="overview-grid">
      <section>
        <div class="section-heading">
          <img class="heading-icon" :src="authIcon" alt="基础信息" />
          <span>基础信息</span>
        </div>
        <div class="info-card">
          <div class="info-grid">
            <div v-for="item in baseInfo" :key="item.label" class="info-item">
              <div class="info-label">{{ item.label }}</div>
              <div class="info-value">{{ item.value }}</div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div class="section-heading">
          <img class="heading-icon" :src="operationIcon" alt="运营概况" />
          <span>运营概况</span>
        </div>
        <div class="info-card operation-card">
          <div v-for="item in operationInfo" :key="item.label" class="info-item operation-item">
            <div class="info-label">{{ item.label }}</div>
            <div class="info-value multi-line">{{ item.value }}</div>
          </div>
        </div>
      </section>
    </div>

    <section class="auth-section">
      <div class="section-heading auth-heading">
        <img class="heading-icon" :src="authIcon" alt="机构身份认证" />
        <span>机构身份认证</span>
      </div>

      <div class="auth-process">
        <div class="timeline-line"></div>
        <div class="process-step current">
          <span class="dot"></span>
          <div class="step-content">
            <div class="step-row">
              <strong>基础信息填写</strong>
              <span class="badge">必填</span>
            </div>
            <p>需上传营业执照，主播授权协议等资质</p>
          </div>
          <button type="button" class="verify-btn" :disabled="certificationStatus === 'pending'" @click="goVerify">{{ verifyButtonText }}</button>
        </div>

        <div class="process-step " :class="certificationStatus != 'approved'?'pending':'current'">
          <span class="dot"></span>
          <div class="step-content">
            <div class="step-row">
              <strong>平台审核</strong>
              <span class="badge light">{{ authStatusLabel }}</span>
            </div>
            <p>审核通过后可批量导入旗下主播账号，绑定归属关系</p>
            <p v-if="certificationStatus === 'rejected' && authRejectReason" class="reject-reason">
				驳回原因：
				<span class="text_red">{{ authRejectReason }}</span>
			</p>
          </div>
        </div>
      </div>
    </section>
  </UserShell>
</template>

<script>
import UserShell from './components/UserShell.vue';
import sidebarBg from '@/assets/img/user/背景卡图.png';
import userAvatar from '@/assets/img/user/avatar.png';
import overviewActiveIcon from '@/assets/img/user/griconxz.png';
import overviewActiveIconIn from '@/assets/img/user/gricon.png';
import talentActiveIcon from '@/assets/img/user/zbglxz.png';
import talentInactiveIcon from '@/assets/img/user/zbgl.png';
import assetActiveIcon from '@/assets/img/user/申请icon.png';
import assetInactiveIcon from '@/assets/img/user/申请未选中.png';
import authIcon from '@/assets/img/user/认证icon.png';
import operationIcon from '@/assets/img/user/运营icon.png';
import {
  getMemberCertification,
  getMemberProfile,
  updateMemberAvatar,
  uploadMemberImage
} from '@/api/user';
import { updateCachedUserInfo } from '@/utils/userInfo';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

function createMenu() {
  return [
    { key: 'overview', label: '首页概览', path: '/user/org', activeIcon: overviewActiveIcon,inactiveIcon: overviewActiveIcon},
    { key: 'talent', label: '主播管理', path: '/user/org/talents', activeIcon: talentActiveIcon, inactiveIcon: talentInactiveIcon },
    { key: 'asset', label: '资产评估申请', path: '/user/org/assets', activeIcon: assetActiveIcon, inactiveIcon: assetInactiveIcon }
  ];
}

function extractResponseData(response) {
  return response && response.data ? response.data : {};
}

function extractUploadUrl(response) {
  const data = response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'data')
    ? response.data
    : response;

  if (typeof data === 'string') {
    return data;
  }

  if (!data || typeof data !== 'object') {
    return '';
  }

  return data.url || data.full_url || data.path || data.location || '';
}

function formatDate(value, withTime = false) {
  if (!value) {
    return '--';
  }

  const source = String(value).trim();
  return withTime ? source : source.slice(0, 10);
}

function formatPlatforms(platforms) {
  if (!Array.isArray(platforms) || !platforms.length) {
    return '--';
  }

  const labels = platforms
    .map(item => (item && (item.platform_text || item.platform)) ? String(item.platform_text || item.platform).trim() : '')
    .filter(Boolean);

  return labels.length ? Array.from(new Set(labels)).join('、') : '--';
}

function getCertificationStatusLabel(status) {
  if (status === 'pending') {
    return '待审核';
  }
  if (status === 'approved') {
    return '已通过';
  }
  if (status === 'rejected') {
    return '已驳回';
  }
  return '约2个工作日';
}

function syncCachedUserInfo(profileData) {
  try {
    const raw = window.localStorage.getItem('userInfo');
    const cachedUserInfo = raw ? JSON.parse(raw) : {};
    const nextUserInfo = {
      ...cachedUserInfo,
      avatar: profileData.avatar || cachedUserInfo.avatar || '',
      name: profileData.name || cachedUserInfo.name || '',
      type: profileData.type || cachedUserInfo.type || '',
      type_text: profileData.type_text || cachedUserInfo.type_text || '',
      phone: profileData.phone || cachedUserInfo.phone || '',
      created_at: profileData.created_at || cachedUserInfo.created_at || '',
      company_code: profileData.company_code || cachedUserInfo.company_code || ''
    };
    updateCachedUserInfo(nextUserInfo);
  } catch (error) {
    void error;
  }
}

export default {
  name: 'OrganizationCenter',
  components: { UserShell },
  computed: {
    verifyButtonText() {
      if (this.certificationStatus === 'pending') {
        return '审核中';
      }
      if (this.certificationStatus === 'approved') {
        return '已认证';
      }
      if (this.certificationStatus === 'rejected') {
        return '重新提交';
      }
      return '去认证';
    }
  },
  data() {
    return {
      authIcon,
      operationIcon,
      profile: {
        name: '星辰传媒有限公司',
        role: 'MCN机构',
        avatar: userAvatar,
        background: sidebarBg
      },
      companyInfo: {
        name: '星辰传媒有限公司',
        entryDate: '2025-08-12'
      },
      menuItems: createMenu(),
      authStatusLabel: '约2个工作日',
      certificationStatus: '',
      authRejectReason: '',
      baseInfo: [],
      operationInfo: []
    };
  },
  created() {
    this.fetchOrganizationData();
  },
  methods: {
    async fetchOrganizationData() {
      try {
        const [certificationResponse, profileResponse] = await Promise.all([
          getMemberCertification(),
          getMemberProfile()
        ]);
        this.applyOrganizationData(
          extractResponseData(certificationResponse),
          extractResponseData(profileResponse)
        );
      } catch (error) {
        if (error && error.isAuthExpired) {
          return;
        }
        this.$message.error(error && error.message ? error.message : '获取机构信息失败');
      }
    },
    applyOrganizationData(certificationData, profileData) {
      const organizationName = profileData.name || 'MCN机构';
      const profileAvatar = profileData.avatar || this.profile.avatar || userAvatar;
      this.certificationStatus = certificationData.status || '';
      this.authStatusLabel = certificationData.status_text || getCertificationStatusLabel(this.certificationStatus);
      this.authRejectReason = certificationData.reject_reason || '';
      this.profile = {
        ...this.profile,
        name: organizationName,
        role: profileData.type_text || 'MCN机构',
        avatar: profileAvatar
      };
      this.companyInfo = {
        name: organizationName,
        entryDate: formatDate(profileData.created_at)
      };
      this.baseInfo = [
        { label: '企业名称', value: organizationName },
        { label: '注册手机号', value: profileData.phone || '--' },
        { label: '注册时间', value: formatDate(profileData.created_at, true) },
        { label: '统一社会信用代码', value: profileData.company_code || '--' }
      ];
      this.operationInfo = [
        { label: '覆盖平台', value: formatPlatforms(profileData.platforms) },
        { label: '签约主播总数', value: `${profileData.streamer_count || 0}位` }
      ];
      syncCachedUserInfo(profileData);
    },
    triggerAvatarUpload() {
      const target = this.$refs.avatarUploader;
      if (target) {
        target.click();
      }
    },
    async handleAvatarChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) {
        return;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        this.$message.warning('图片大小不能超过5M');
        event.target.value = '';
        return;
      }

      try {
        const uploadResponse = await uploadMemberImage(file);
        const avatarUrl = extractUploadUrl(uploadResponse);
        if (!avatarUrl) {
          throw new Error('上传成功但未获取到头像地址');
        }

        await updateMemberAvatar({ avatar: avatarUrl });
        this.profile = {
          ...this.profile,
          avatar: avatarUrl
        };
        syncCachedUserInfo({
          avatar: avatarUrl,
          name: this.profile.name,
          type: 'mcn',
          type_text: this.profile.role,
          phone: this.baseInfo[1] ? this.baseInfo[1].value : '',
          created_at: this.baseInfo[2] ? this.baseInfo[2].value : '',
          company_code: this.baseInfo[3] ? this.baseInfo[3].value : ''
        });
        this.$message.success('头像已更新');
      } catch (error) {
        this.$message.error(error && error.message ? error.message : '头像更新失败');
      } finally {
        event.target.value = '';
      }
    },
    goVerify() {
      if (this.certificationStatus === 'pending') {
        this.$message.warning('认证资料审核中');
        return;
      }
      this.$router.push('/user/org/auth');
    }
  }
};
</script>

<style lang="scss" scoped>
.hero-row {
  display: flex;
  align-items: center;
  gap: 22px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e6e8ee;
  margin-top: 28px;
}

.hero-avatar {
  width: 82px;
  height: 82px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, #9f64ff, #528aff);
  padding: 2px;
  box-sizing: border-box;
  display: block;
}

.hero-avatar-btn {
  position: relative;
  width: 82px;
  height: 82px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  overflow: hidden;
}

.hero-avatar-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.58);
  color: #fff;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.hero-avatar-btn:hover .hero-avatar-mask {
  opacity: 1;
}

.hidden-input {
  display: none;
}
.hero-avatar-mask-icon {
  width: 16px;
  height: 16px;
}


.hero-name-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hero-name {
  font-size: 22px;
  font-weight: 600;
  color: #1e293b;
}

.hero-subline {
  margin-top: 10px;
  font-size: 14px;
  color: #84899e;
}

.mcn-tag {
  width: 92px;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 34px;
  margin-top: 30px;
}

.section-heading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #1e293b;
  font-size: 16px;
  font-weight: 600;
}

.heading-icon {
  width: 18px;
  height: 18px;
}

.info-card {
  margin-top: 18px;
  min-height: 150px;
  padding: 26px 24px 20px;
  border-radius: 12px;
  background: #f8f9fc;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 26px 28px;
}

.info-label {
  font-size: 14px;
  color: #a2aac4;
}

.info-value {
  margin-top: 10px;
  font-weight: 500;
  font-size: 14px;
  color: #1E293B;
  line-height: 24px;
  text-align: left;
}

.multi-line {
  line-height: 1.8;
}

.operation-item + .operation-item {
  margin-top: 20px;
}

.auth-section {
  margin-top: 34px;
  padding-top: 6px;
}

.auth-heading {
  margin-bottom: 26px;
}

.auth-process {
  position: relative;
  padding-left: 10px;
}

.timeline-line {
  position: absolute;
  left: 15px;
  top: 30px;
  width: 1px;
  height: 92px;
  border-left: 1px dashed #d7dce8;
}

.process-step {
  position: relative;
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) auto;
  gap: 3px;
}

.process-step + .process-step {
  margin-top: 30px;
}

.dot {
  width: 10px;
  height: 10px;
  margin-top: 10px;
  border-radius: 50%;
  background: #d7dce8;
}

.process-step.current .dot {
  background: #3f73ff;
  box-shadow: 0 0 0 3px rgba(63, 115, 255, 0.12);
}

.step-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-row strong {
  font-size: 14px;
  font-weight: 600;
  color: #3D3D3D;
}

.badge {
  min-width: 37px;
  height: 16px;
  padding: 0 10px;
  border-radius: 2px;
  background: #D1E0FA;
  color: #0857DF;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
}

.badge.light {
  min-width: 80px;
}

.step-content p {
  margin-top: 10px;
  font-size: 14px;
  color: #818181;
  line-height: 22px;
}

.text_red {
  color: #e34d59 !important;
}
	
.bj-tag{
	width: 10px;
	height: 10px;
}
.verify-btn {
  align-self: center;
  width: 94px;
  height: 32px;
  border: 0;
  border-radius: 4px;
  background: #5754CD;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
}

.verify-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

@media (max-width: 1200px) {
  .overview-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .hero-row,
  .hero-name-row,
  .process-step,
  .step-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-grid,
  .process-step {
    grid-template-columns: 1fr;
  }

  .timeline-line {
    display: none;
  }
}
</style>
